import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { formatINR, apiErrorMsg } from '@/lib/formatters';
import { Building2, MapPin, Plus, Eye, Grid3X3, Calendar, ChevronRight } from 'lucide-react';

const UNIT_STATUS_COLORS = {
  available: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  on_hold: 'bg-amber-100 text-amber-800 border-amber-200',
  token_pending: 'bg-purple-100 text-purple-800 border-purple-200',
  booked: 'bg-blue-100 text-blue-800 border-blue-200',
  sold: 'bg-slate-200 text-slate-700 border-slate-300',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
};

export default function ProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    api.get('/projects').then(r => { setProjects(r.data.projects); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const viewInventory = async (project) => {
    setSelectedProject(project);
    setUnitsLoading(true);
    try {
      const { data } = await api.get(`/units/${project._id}`);
      setUnits(data.units);
    } catch { toast.error('Failed to load units'); }
    finally { setUnitsLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Projects & Inventory</h1>
          <p className="text-slate-500 text-sm mt-0.5">{projects.length} active projects</p>
        </div>
        {user?.role === 'director' && (
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-project-btn">
            <Plus size={16} /> Add Project
          </Button>
        )}
      </div>

      {!selectedProject ? (
        /* Project List */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            [1, 2, 3].map(i => <div key={i} className="h-56 bg-white rounded-xl border animate-pulse" />)
          ) : projects.length === 0 ? (
            <Card className="col-span-full"><CardContent className="py-12 text-center text-slate-400">No projects yet</CardContent></Card>
          ) : (
            projects.map(project => (
              <Card key={project._id} className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer group" onClick={() => viewInventory(project)} data-testid={`project-card-${project.code}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-bold group-hover:text-emerald-600 transition-colors">{project.name}</CardTitle>
                      <CardDescription className="flex items-center gap-1 mt-1">
                        <Building2 size={12} /> {project.developer}
                      </CardDescription>
                    </div>
                    <Badge variant="outline" className="text-xs">{project.code}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-1 text-sm text-slate-500">
                    <MapPin size={14} /> <span className="truncate">{project.address}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {project.configurations?.map(c => (
                      <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
                    ))}
                  </div>
                  {/* Unit Summary */}
                  <div className="flex items-center gap-3 text-xs pt-2 border-t border-slate-100">
                    <span className="text-slate-500">Units: <strong className="text-slate-700">{project.total_units || 0}</strong></span>
                    {project.unit_summary?.available > 0 && (
                      <span className="text-emerald-600">Available: <strong>{project.unit_summary.available}</strong></span>
                    )}
                    {project.unit_summary?.booked > 0 && (
                      <span className="text-blue-600">Booked: <strong>{project.unit_summary.booked}</strong></span>
                    )}
                    {project.unit_summary?.sold > 0 && (
                      <span className="text-slate-600">Sold: <strong>{project.unit_summary.sold}</strong></span>
                    )}
                  </div>
                  <div className="flex items-center text-emerald-600 text-sm font-medium pt-1">
                    View Inventory <ChevronRight size={16} />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      ) : (
        /* Inventory View */
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" onClick={() => setSelectedProject(null)} className="text-sm">
              Projects
            </Button>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="font-semibold text-slate-800">{selectedProject.name}</span>
            <Badge variant="outline" className="text-xs">{selectedProject.code}</Badge>
          </div>

          {/* Project Details Card */}
          <Card className="border-slate-200 mb-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Developer</p>
                  <p className="font-medium">{selectedProject.developer}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">RERA</p>
                  <p className="font-medium text-xs">{selectedProject.rera_number || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Base Price</p>
                  <p className="font-medium">{formatINR(selectedProject.base_price_per_sqft)}/sqft</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Possession</p>
                  <p className="font-medium">{selectedProject.possession_date || '-'}</p>
                </div>
              </div>
              {selectedProject.description && (
                <p className="text-sm text-slate-500 mt-3 border-t border-slate-100 pt-3">{selectedProject.description}</p>
              )}
            </CardContent>
          </Card>

          {/* Unit Summary */}
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(
              units.reduce((acc, u) => { acc[u.status] = (acc[u.status] || 0) + 1; return acc; }, {})
            ).map(([status, count]) => (
              <span key={status} className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${UNIT_STATUS_COLORS[status] || 'bg-slate-100'}`}>
                {status.replace(/_/g, ' ')}: {count}
              </span>
            ))}
          </div>

          {/* Units Table */}
          <div className="table-container bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Unit</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Tower</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Floor</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Config</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Carpet Area</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Price</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unitsLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>{Array.from({ length: 7 }).map((_, j) => <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>)}</TableRow>
                  ))
                ) : units.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No units found</TableCell></TableRow>
                ) : (
                  units.map(unit => (
                    <TableRow key={unit._id} data-testid={`unit-row-${unit.unit_number}`}>
                      <TableCell className="font-mono text-sm font-semibold">{unit.unit_number}</TableCell>
                      <TableCell className="text-sm">{unit.tower}</TableCell>
                      <TableCell className="text-sm">{unit.floor}</TableCell>
                      <TableCell className="text-sm">{unit.configuration}</TableCell>
                      <TableCell className="text-sm">{unit.carpet_area} sqft</TableCell>
                      <TableCell className="text-sm font-semibold">{formatINR(unit.selling_price)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${UNIT_STATUS_COLORS[unit.status] || ''}`}>
                          {unit.status?.replace(/_/g, ' ')}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Create Project Dialog */}
      {showCreate && <CreateProjectDialog open onClose={() => setShowCreate(false)} onCreated={() => { api.get('/projects').then(r => setProjects(r.data.projects)); setShowCreate(false); }} />}
    </div>
  );
}

function CreateProjectDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', code: '', developer: '', rera_number: '', address: '', description: '', base_price_per_sqft: '', configurations: '', possession_date: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/projects', {
        ...form,
        base_price_per_sqft: Number(form.base_price_per_sqft) || 0,
        configurations: form.configurations.split(',').map(s => s.trim()).filter(Boolean),
      });
      toast.success('Project created');
      onCreated();
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add New Project</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Project Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="project-name-input" /></div>
            <div><Label>Code *</Label><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="e.g. SH-2025" required data-testid="project-code-input" /></div>
            <div><Label>Developer</Label><Input value={form.developer} onChange={e => setForm({ ...form, developer: e.target.value })} /></div>
            <div><Label>RERA Number</Label><Input value={form.rera_number} onChange={e => setForm({ ...form, rera_number: e.target.value })} /></div>
            <div><Label>Base Price/sqft</Label><Input type="number" value={form.base_price_per_sqft} onChange={e => setForm({ ...form, base_price_per_sqft: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Configs (comma-sep)</Label><Input value={form.configurations} onChange={e => setForm({ ...form, configurations: e.target.value })} placeholder="2 BHK, 3 BHK" /></div>
            <div><Label>Possession Date</Label><Input type="date" value={form.possession_date} onChange={e => setForm({ ...form, possession_date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-project-submit">{saving ? 'Creating...' : 'Create Project'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
